/**
 * __root.tsx redirect-cascade pure helper.
 *
 * Extracted by signup-billing-mvp Phase 5 Session 7 (2026-05-13) so the
 * 7-branch precedence ladder is unit-testable in isolation without
 * rendering the route. The matrix test in `__tests__/__root.helpers.test.ts`
 * exercises every branch end-to-end.
 *
 * Pre-Session-7, `__root.tsx` carried 4 separate `useEffect` redirect blocks
 * (~L64-105) that interleaved auth-loading-gate-checks with navigate calls.
 * Open Decision 2 of the Phase 5 design consolidated them into a single
 * useEffect that calls this pure function and navigates iff `target !== null
 * && target !== currentPath`.
 *
 * **Precedence (top to bottom)**:
 *
 *   1. `!state.user` → redirect to `/login` unless on an auth-route OR
 *      public-route (already there OR the deal page chrome handles its own).
 *   2. `needsOnboarding(profile)` → redirect to `/signup` unless already
 *      there (the 6-question wizard branch). MUST run before the pending
 *      branch — invited users are `status='pending' + onboarding_completed=
 *      false` and need to reach the wizard.
 *   3. `profile?.status === 'pending' | 'rejected'` → return null
 *      (caller renders `<PendingApprovalPage>` in place of `<Outlet/>`).
 *   4. `state.isAdmin` → return null (admin bypass — admins never get
 *      redirected to `/billing/checkout` regardless of `state.balance`).
 *      Auth routes for admins bounce to `/cockpit`.
 *   5. `!state.balance` → redirect to `/billing/checkout` (unless already
 *      there). Approved + non-admin + no subscription = must pay.
 *      When `state.conversation` is set, preserves it as `?conversation=<id>`
 *      so post-subscribe nav can resume the shared deal.
 *   6. `state.balance.locked` → same as #5.
 *   7. Default — render shell (null). Bounce away from auth routes to
 *      `/cockpit` if the user somehow lands on `/login` while signed-in.
 *
 * The pure function never reads from `window`, never navigates, never
 * touches React state. Caller is responsible for the side-effects.
 *
 * **conversation-preservation (in-app-checkout Phase 3 Session 3.7)**:
 * Branches 5+6 previously returned the bare string `/billing/checkout`,
 * dropping the `?conversation=` param the user arrived with. The fix adds
 * `conversation?: string` to `AuthState` so the cascade can thread it
 * through to `/billing/checkout?conversation=<id>`. Without this, clicking
 * a public shared-deal CTA (which carries `?conversation=<uuid>`) on a user
 * with no subscription would land them at the pricing grid with no
 * post-subscribe deep-link context — they'd end up at `/cockpit` after
 * subscribing instead of returning to the shared deal.
 */

import type { User } from '@supabase/supabase-js';
import type { BillingUsageResponse } from '@/hooks/use-billing-usage';
import { asBillingUsageRow } from '@/hooks/use-billing-usage';

/**
 * Minimal projection of `@arcanum-hq/shared-types` `UserProfile` used by
 * the cascade. Locally declared so the helper file has zero runtime
 * dependencies that hit the schema package.
 */
export interface CascadeUserProfile {
  status: 'pending' | 'approved' | 'rejected';
  onboarding_completed: boolean;
}

export interface AuthState {
  user: User | null;
  isAdmin: boolean;
  profile: CascadeUserProfile | null | undefined;
  balance: BillingUsageResponse | null | undefined;
  currentPath: string;
  /**
   * Optional deep-link conversation id from the current URL's `?conversation=`
   * search param. When present and the cascade redirects to `/billing/checkout`
   * (branches 5+6), it is threaded through as `?conversation=<id>` so
   * post-subscribe navigation can resume the shared deal instead of landing
   * at `/cockpit`. (in-app-checkout Phase 3 Session 3.7)
   */
  conversation?: string;
}

const AUTH_ROUTES: readonly string[] = [
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
];

/**
 * Public route prefixes that bypass the entire auth-guard chain (no
 * redirect to `/login`, no approval gate, no onboarding redirect, no
 * billing redirect). Mirrors `__root.tsx`'s `isPublicRoute` predicate.
 */
export function isPublicRoute(pathname: string): boolean {
  // DEV-only `ui-designer` prototype harness (`/prototype`, `/prototype/<slug>`)
  // bypasses the auth-guard chain so prototypes render full-bleed without a
  // login or backend. `import.meta.env.DEV` is statically `false` in production
  // builds, so Vite folds this clause away (the routes aren't registered in
  // prod either — see App.tsx). See `.claude/agents/ui-designer.md`.
  if (import.meta.env.DEV && pathname.startsWith('/prototype')) return true;
  return pathname.startsWith('/deal/') || pathname.startsWith('/auth/callback');
}

export function isAuthRoute(pathname: string): boolean {
  return AUTH_ROUTES.includes(pathname);
}

/** True when the profile exists and `onboarding_completed === false`. */
export function needsOnboarding(
  profile: CascadeUserProfile | null | undefined
): boolean {
  return !!profile && profile.onboarding_completed === false;
}

/**
 * Compute the next route to navigate to. Returns null when:
 *   - the current path is already the right destination, OR
 *   - the caller should RENDER a non-Outlet branch in place
 *     (pending/rejected → `<PendingApprovalPage>`).
 *
 * The caller (`__root.tsx`) navigates iff `target && target !== currentPath`.
 */
export function computeRedirectTarget(state: AuthState): string | null {
  const path = state.currentPath;

  // Step 1 — unauthenticated.
  if (!state.user) {
    if (isAuthRoute(path) || isPublicRoute(path)) return null;
    return '/login';
  }

  // Step 2 — needs onboarding (must run BEFORE the pending/rejected branch
  // because invited users are created with status='pending' AND
  // onboarding_completed=false; they must reach the /signup wizard to fire
  // APPROVAL_REQUESTED. Without this ordering, invited users get stuck on
  // PendingApprovalPage forever. signup-billing-mvp Phase 5 carryover bug,
  // surfaced + fixed 2026-05-14.)
  if (needsOnboarding(state.profile)) {
    return path === '/signup' ? null : '/signup';
  }

  // Step 3 — pending or rejected → caller renders <PendingApprovalPage>
  // in place. The bare root path stays on the pending page; explicit auth
  // routes (`/login`, etc.) and public routes pass through unchanged.
  if (
    state.profile?.status === 'pending' ||
    state.profile?.status === 'rejected'
  ) {
    if (isAuthRoute(path) || isPublicRoute(path)) return null;
    return null;
  }

  // Step 4 — admin bypass. Admins never get redirected to `/billing/checkout`.
  // Auth routes for admins bounce to `/cockpit` (mirrors the default branch).
  if (state.isAdmin) {
    if (isAuthRoute(path) || path === '/') return '/cockpit';
    return null;
  }

  // Step 5 + 6 — approved, non-admin, billing checks. Both surface the same
  // `/billing/checkout` redirect; differentiated only for telemetry/intent.
  // When `state.conversation` is set, thread it as `?conversation=<id>` so
  // the post-subscribe navigation returns to the shared deal. (in-app-checkout
  // Phase 3 Session 3.7 — conversation-preservation fix.)
  const checkoutPath = state.conversation
    ? `/billing/checkout?conversation=${encodeURIComponent(state.conversation)}`
    : '/billing/checkout';
  const row = asBillingUsageRow(state.balance ?? null);
  // No subscription row → must pay (or in the middle of checkout).
  if (!row) {
    if (isPublicRoute(path)) return null;
    return path === '/billing/checkout' ? null : checkoutPath;
  }
  // Locked subscription → admin override OR Stripe subscription deleted.
  if (row.locked) {
    if (isPublicRoute(path)) return null;
    return path === '/billing/checkout' ? null : checkoutPath;
  }

  // Step 7 — default: bounce away from auth/bare-root to `/cockpit`; else
  // render the shell.
  if (isAuthRoute(path) || path === '/') return '/cockpit';
  return null;
}
